import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { generateEventId, trackMetaLead } from '../lib/metaTracking';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    hvorfra: '',
    hvortil: '',
    service_type: 'Autotransport',
    message: '',
    website_url: '' // Honeypot
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const eventIdRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg('');

    // Generate or reuse eventId across retries for deduplication
    if (!eventIdRef.current) {
      eventIdRef.current = generateEventId();
    }
    const currentEventId = eventIdRef.current;

    try {
      const payload = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        hvorfra: formData.hvorfra,
        hvortil: formData.hvortil,
        message: `Ydelse: ${formData.service_type}\nHvorfra: ${formData.hvorfra || '-'}\nHvortil: ${formData.hvortil || '-'}\n\nBesked:\n${formData.message}`,
        website_url: formData.website_url,
        service_subject: formData.service_type
      };

      const res = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) throw new Error('Kunne ikke indsende formularen. Ring venligst direkte på 70 700 800.');

      // Fire deduplicated Lead event to both Meta Pixel & Meta Conversions API
      await trackMetaLead({
        eventId: currentEventId,
        email: formData.email,
        phone: formData.phone,
        contentName: `Kontakt: ${formData.service_type}`
      });

      // Clear event ID after success so subsequent new form fills receive a new ID
      eventIdRef.current = null;

      setSubmitted(true);
      setFormData({
        name: '',
        phone: '',
        email: '',
        hvorfra: '',
        hvortil: '',
        service_type: 'Autotransport',
        message: '',
        website_url: ''
      });
    } catch (err) {
      setErrorMsg(err.message || 'Der opstod en fejl.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Kontakt MC Transport | Vagtcentral 24/7 & Tilbud"
        description="Kontakt MC Transport på 70 700 800. Døgnbemandet vagtcentral 24/7 og afdelinger i Ugerløse, Odense og Hedensted. Få et uforpligtende tilbud på din opgave."
      />

      {/* Hero */}
      <section className="bg-zinc-950 text-white py-16 lg:py-20 border-b border-zinc-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(220,38,38,0.2),transparent_70%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <span className="text-xs font-bold uppercase tracking-widest text-red-500 font-condensed mb-2 block">
            Døgnåben Vagtcentral 24/7/365 · Landsdækkende
          </span>
          <h1 className="font-condensed text-4xl sm:text-6xl font-extrabold uppercase tracking-tight text-white">
            Kontakt MC Transport
          </h1>
          <p className="mt-4 text-base sm:text-lg text-zinc-300 max-w-2xl mx-auto">
            Hos <Link to="/" className="font-semibold text-white hover:text-red-400 underline decoration-red-500 underline-offset-2 transition-colors">MC Transport</Link> sidder vores døgnåbne vagtcentral klar ved telefonen og tasterne – uanset om du har brug for akut <Link to="/autohjaelp" className="text-red-400 hover:text-red-300 underline decoration-red-400/40 underline-offset-2">autohjælp 24/7</Link>, <Link to="/bjaergning" className="text-red-400 hover:text-red-300 underline decoration-red-400/40 underline-offset-2">bjærgning</Link> eller et uforpligtende tilbud på en planlagt transportopgave.
          </p>
        </div>
      </section>

      {/* Quick 3 Contact Cards: Phone, Mail, Facebook */}
      <section className="bg-zinc-900 border-b border-zinc-800 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Phone */}
          <div className="bg-zinc-950 p-6 rounded-xl border border-zinc-800 flex items-center gap-4">
            <div className="w-14 h-14 rounded-lg bg-red-600 flex items-center justify-center text-white text-2xl shrink-0 shadow">
              <i className="fa-solid fa-phone" aria-hidden="true" />
            </div>
            <div>
              <span className="text-xs uppercase font-bold text-red-400 tracking-wider">Vagtcentral 24/7</span>
              <a
                href="tel:+4570700800"
                className="block font-condensed text-2xl lg:text-3xl font-extrabold text-white hover:text-red-400 transition-colors whitespace-nowrap"
              >
                +45 70 700 800
              </a>
              <span className="text-xs text-zinc-400">Døgnåben hele året</span>
            </div>
          </div>

          {/* Email */}
          <div className="bg-zinc-950 p-6 rounded-xl border border-zinc-800 flex items-center gap-4">
            <div className="w-14 h-14 rounded-lg bg-zinc-800 flex items-center justify-center text-red-500 text-2xl shrink-0 border border-zinc-700 shadow">
              <i className="fa-solid fa-envelope" aria-hidden="true" />
            </div>
            <div className="truncate">
              <span className="text-xs uppercase font-bold text-zinc-400 tracking-wider">E-mail</span>
              <a
                href="mailto:kontor@mcautotransport.dk"
                className="block font-condensed text-lg lg:text-xl font-bold text-white hover:text-red-400 transition-colors truncate whitespace-nowrap"
              >
                kontor@mcautotransport.dk
              </a>
              <span className="text-xs text-zinc-400">Hurtigt skriftligt svar</span>
            </div>
          </div>

          {/* Facebook */}
          <div className="bg-zinc-950 p-6 rounded-xl border border-zinc-800 flex items-center gap-4">
            <div className="w-14 h-14 rounded-lg bg-[#1877f2] flex items-center justify-center text-white text-3xl shrink-0 shadow">
              <i className="fa-brands fa-facebook" aria-hidden="true" />
            </div>
            <div>
              <span className="text-xs uppercase font-bold text-blue-400 tracking-wider">Facebook</span>
              <a
                href="https://www.facebook.com/MCautotransport/"
                target="_blank"
                rel="noopener noreferrer"
                className="block font-condensed text-2xl lg:text-3xl font-extrabold text-white hover:text-blue-300 transition-colors"
              >
                MC Autotransport
              </a>
              <span className="text-xs text-zinc-400">Følg vores hverdag</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Section: Form & Locations */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Locations: Three Locations & Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-16">
            {/* Location 1: Ugerløse */}
            <div className="bg-zinc-50 rounded-2xl p-6 sm:p-7 border border-zinc-200 shadow-sm flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <span className="text-[11px] uppercase font-bold text-red-600 tracking-wider font-condensed">
                      Hovedafdeling & Sikret Plads
                    </span>
                    <h3 className="font-condensed text-2xl font-bold uppercase text-zinc-950">
                      MC Transport – Ugerløse
                    </h3>
                    <p className="text-sm text-zinc-700 mt-1">
                      Annexgårdsvej 7, 4350 Ugerløse
                    </p>
                  </div>
                  <a
                    href="https://www.google.com/maps/dir//Annexg%C3%A5rdsvej+7,+4350+Ugerl%C3%B8se"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3.5 py-2 bg-zinc-900 hover:bg-red-600 text-white font-condensed text-xs font-bold uppercase rounded shadow transition-colors shrink-0 flex items-center gap-1.5"
                  >
                    <i className="fa-solid fa-diamond-turn-right" aria-hidden="true" />
                    <span>Rute</span>
                  </a>
                </div>

                <div className="text-xs text-zinc-600 space-y-1.5 mb-4">
                  <p>• 80.000 m² sikret udendørs plads og 6.000 m² haller (<Link to="/om-os" className="text-red-600 hover:underline font-semibold">læs om os</Link>)</p>
                  <p>• 64 overvågningskameraer med infrarød natvisning</p>
                  <p>• 24/7 automatisk sluse til transportører</p>
                  <p>• Sikret plads til <Link to="/bilopbevaring" className="text-red-600 hover:underline font-semibold">bilopbevaring</Link></p>
                </div>
              </div>

              {/* Google Maps embed Ugerløse */}
              <div className="w-full h-52 rounded-lg overflow-hidden border border-zinc-300 mt-2">
                <iframe
                  title="Google Map MC Transport Ugerløse"
                  src="https://maps.google.com/maps?q=Annexg%C3%A5rdsvej%207,%204350%20Ugerl%C3%B8se&t=&z=13&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            {/* Location 2: Odense (Tilføjet mellem Ugerløse og Hedensted) */}
            <div className="bg-zinc-50 rounded-2xl p-6 sm:p-7 border border-zinc-200 shadow-sm flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <span className="text-[11px] uppercase font-bold text-red-600 tracking-wider font-condensed">
                      Afdeling Fyn
                    </span>
                    <h3 className="font-condensed text-2xl font-bold uppercase text-zinc-950">
                      MC Transport – Odense
                    </h3>
                    <p className="text-sm text-zinc-700 mt-1">
                      Energivej 36, 5260 Odense
                    </p>
                  </div>
                  <a
                    href="https://www.google.com/maps/dir//Energivej+36,+5260+Odense"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3.5 py-2 bg-zinc-900 hover:bg-red-600 text-white font-condensed text-xs font-bold uppercase rounded shadow transition-colors shrink-0 flex items-center gap-1.5"
                  >
                    <i className="fa-solid fa-diamond-turn-right" aria-hidden="true" />
                    <span>Rute</span>
                  </a>
                </div>

                <div className="text-xs text-zinc-600 space-y-1.5 mb-4">
                  <p>• Centralt placeret ved det fynske motorvejsnet (E20)</p>
                  <p>• Hurtig udrykning og <Link to="/ydelser/autotransport-ydelser" className="text-red-600 hover:underline font-semibold">autotransport</Link> på Fyn og øerne</p>
                  <p>• Døgnbemandet vagtcentral på telefon <a href="tel:+4570700800" className="text-red-600 hover:underline font-semibold">70 700 800</a></p>
                  <p>• Vejhjælp, bugsering og transport</p>
                </div>
              </div>

              {/* Google Maps embed Odense */}
              <div className="w-full h-52 rounded-lg overflow-hidden border border-zinc-300 mt-2">
                <iframe
                  title="Google Map MC Transport Odense"
                  src="https://maps.google.com/maps?q=Energivej%2036,%205260%20Odense&t=&z=13&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            {/* Location 3: Hedensted */}
            <div className="bg-zinc-50 rounded-2xl p-6 sm:p-7 border border-zinc-200 shadow-sm flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <span className="text-[11px] uppercase font-bold text-red-600 tracking-wider font-condensed">
                      Afdeling Jylland
                    </span>
                    <h3 className="font-condensed text-2xl font-bold uppercase text-zinc-950">
                      MC Transport – Hedensted
                    </h3>
                    <p className="text-sm text-zinc-700 mt-1">
                      Lundagervej 4, 8722 Hedensted
                    </p>
                  </div>
                  <a
                    href="https://www.google.com/maps/dir//Lundagervej+4,+8722+Hedensted"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3.5 py-2 bg-zinc-900 hover:bg-red-600 text-white font-condensed text-xs font-bold uppercase rounded shadow transition-colors shrink-0 flex items-center gap-1.5"
                  >
                    <i className="fa-solid fa-diamond-turn-right" aria-hidden="true" />
                    <span>Rute</span>
                  </a>
                </div>

                <div className="text-xs text-zinc-600 space-y-1.5 mb-4">
                  <p>• Strategisk placeret ved motorvejsnettet i Trekantområdet</p>
                  <p>• Hurtig udrykning og <Link to="/ydelser/autotransport-ydelser" className="text-red-600 hover:underline font-semibold">autotransport</Link> i Jylland</p>
                  <p>• Døgnservice til erhverv og private via vores <Link to="/ydelser" className="text-red-600 hover:underline font-semibold">vognmandsservice</Link></p>
                  <p>• Nyt moderne <Link to="/daekcenter" className="text-red-600 hover:underline font-semibold">dækcenter</Link> åbner 1/11-2026</p>
                </div>
              </div>

              {/* Google Maps embed Hedensted */}
              <div className="w-full h-52 rounded-lg overflow-hidden border border-zinc-300 mt-2">
                <iframe
                  title="Google Map MC Transport Hedensted"
                  src="https://maps.google.com/maps?q=Lundagervej%204,%20Hedensted&t=&z=13&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>

          {/* Contact Form Section */}
          <div className="max-w-4xl mx-auto bg-zinc-50 p-8 sm:p-12 rounded-2xl border border-zinc-200 shadow-lg">
            <div className="text-center max-w-xl mx-auto mb-8">
              <span className="text-xs font-bold uppercase tracking-wider text-red-600 font-condensed">
                Forespørgsel & Tilbud
              </span>
              <h2 className="font-condensed text-3xl sm:text-4xl font-extrabold uppercase text-zinc-950 mt-1 mb-2">
                Få et tilbud på din transportopgave
              </h2>
              <p className="text-xs sm:text-sm text-zinc-600">
                Udfyld formularen herunder, så beregner vi et uforpligtende tilbud på din opgave.
              </p>
            </div>

            {submitted ? (
              <div className="p-6 bg-emerald-50 border border-emerald-300 rounded-lg text-emerald-800 text-center">
                <i className="fa-solid fa-circle-check text-3xl text-emerald-600 mb-2" aria-hidden="true" />
                <h4 className="font-bold text-lg mb-1">Mange tak for din henvendelse!</h4>
                <p className="text-xs sm:text-sm">
                  Vi har modtaget din besked og kontakter dig snarest muligt. Ved akutte opgaver anbefaler vi altid at ringe til vagtcentralen på 70 700 800.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Honeypot field (hidden with sr-only + absolute) */}
                <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
                  <label htmlFor="website_url">Hjemmeside url (lad stå tom)</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website_url}
                    onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-zinc-700 mb-1">
                    Navn *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Dit fulde navn eller firmanavn"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full text-xs p-3 bg-white border border-zinc-300 rounded focus:border-red-600 focus:outline-none"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-zinc-700 mb-1">
                      Telefonnummer *
                    </label>
                    <input
                      type="tel"
                      required
                      placeholder="f.eks. 12 34 56 78"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full text-xs p-3 bg-white border border-zinc-300 rounded focus:border-red-600 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-zinc-700 mb-1">
                      E-mail *
                    </label>
                    <input
                      type="email"
                      required
                      placeholder="din@email.dk"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full text-xs p-3 bg-white border border-zinc-300 rounded focus:border-red-600 focus:outline-none"
                    />
                  </div>
                </div>

                {/* Hvorfra & Hvortil fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-zinc-700 mb-1">
                      Hvorfra
                    </label>
                    <input
                      type="text"
                      placeholder="Afhentningsadresse eller by"
                      value={formData.hvorfra}
                      onChange={(e) => setFormData({ ...formData, hvorfra: e.target.value })}
                      className="w-full text-xs p-3 bg-white border border-zinc-300 rounded focus:border-red-600 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-zinc-700 mb-1">
                      Hvortil
                    </label>
                    <input
                      type="text"
                      placeholder="Leveringsadresse eller by"
                      value={formData.hvortil}
                      onChange={(e) => setFormData({ ...formData, hvortil: e.target.value })}
                      className="w-full text-xs p-3 bg-white border border-zinc-300 rounded focus:border-red-600 focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-zinc-700 mb-1">
                    Hvad drejer henvendelsen sig om?
                  </label>
                  <select
                    value={formData.service_type}
                    onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                    className="w-full text-xs p-3 bg-white border border-zinc-300 rounded focus:border-red-600 focus:outline-none"
                  >
                    <option value="Autotransport">Autotransport</option>
                    <option value="Autohjælp 24/7">Autohjælp 24/7</option>
                    <option value="Bjærgningskøretøj">Bjærgningskøretøj / Bjærgning</option>
                    <option value="Bugsering">Bugsering</option>
                    <option value="Fritrækning">Fritrækning</option>
                    <option value="Krantransport">Krantransport (28 tm)</option>
                    <option value="Maskintransport">Maskintransport</option>
                    <option value="Transport på sengetrailer">Transport på sengetrailer</option>
                    <option value="Gardintrailer m. medbringertruck">Gardintrailer m. medbringertruck</option>
                    <option value="Servicevogn">Servicevogn</option>
                    <option value="Godstransport">Godstransport</option>
                    <option value="Bilopbevaring">Bilopbevaring på sikret plads</option>
                    <option value="Dækcenter">Dækcenter (åbner 1/11-2026 i Hedensted)</option>
                    <option value="Andet">Andet</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-zinc-700 mb-1">
                    Beskrivelse af opgaven *
                  </label>
                  <textarea
                    required
                    rows={5}
                    placeholder="Beskriv hvad der skal transporteres, afhentnings- og leveringsadresse samt særlige forhold..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full text-xs p-3 bg-white border border-zinc-300 rounded focus:border-red-600 focus:outline-none"
                  />
                </div>

                {errorMsg && (
                  <p className="text-xs text-red-600 font-semibold">{errorMsg}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-4 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-condensed text-xl font-bold uppercase tracking-wider rounded shadow transition-colors cursor-pointer"
                >
                  {submitting ? 'Sender forespørgsel...' : 'Send forespørgsel'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
